import {
    CircleIcon,
    Cross2Icon,
    MinusIcon,
    PlusIcon,
    UpdateIcon
} from '@radix-ui/react-icons';
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { DropdownMenuItem } from "../ui/dropdown-menu";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';
import {
    ToggleGroup,
    ToggleGroupItem,
} from '../ui/toggle-group';
import { useForm } from 'react-hook-form';
import api from '../../lib/api';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../ui/use-toast';

export default function AddRegistrationResultDialog({ registration, sportClassId, fetchClass, dropdown, round, action }) {
    const [isLoading, setIsLoading] = useState(false);
    const form = useForm();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [attempts, setAttempts] = useState(['X', 'X', 'X']);
    const [isDNF, setIsDNF] = useState(false);

    const [formData, setFormData] = useState({
        corrections: 0,
        errors: registration.straightCrookedResults?.[round - 1]?.errors || 0,
        time: registration.straightCrookedResults?.[round - 1]?.time || '',
        heightLength: round,
    });

    // Determine the appropriate label or logic based on action
    const updateOrMark = action === "Ändra resultat"
        ? { label: "Ändra resultat", endpoint: "update" }
        : { label: "Lägg till resultat", endpoint: "mark" };

    const isStraightCrooked = sportClassId >= 1 && sportClassId <= 12;

    const submitStraightCrookedResult = async () => {
        setIsLoading(true);
        try {
            const response = await api.post(`https://skhrf-api.cmdigital.se/v1/judges/${updateOrMark.endpoint}-straightcrooked`, {
                registrationId: registration.registrationId,
                resultId: registration.straightCrookedResults?.[round - 1]?.id,
                round: round,
                errors: parseInt(formData.errors),
                time: parseFloat(formData.time),
            });
            toast({ title: response?.data?.message });

            setIsDialogOpen(false);
        } catch (error) {
            toast({ variant: 'destructive', title: 'Något gick fel.', description: error.response?.data?.error });
        } finally {
            setIsLoading(false);
            fetchClass(false);
        }
    };

    const submitHeightLengthResult = async () => {
        if (!formData.heightLength) {
            toast({ variant: 'destructive', title: 'Höjd/Längd saknas.', description: 'Vänligen fyll i höjd eller längd innan du sparar.' });
            return;
        }

        setIsLoading(true);
        try {
            const response = await api.post(`https://skhrf-api.cmdigital.se/v1/judges/${updateOrMark.endpoint}-heightlength`, {
                registrationId: registration.registrationId,
                resultId: registration.heightLengthResults?.find(result =>
                    parseFloat(result.height_length) === parseFloat(round)
                )?.id,
                heightLength: parseFloat(formData.heightLength).toFixed(2),
                attempts: attempts,
            });
            toast({ title: response?.data?.message });

            setIsDialogOpen(false);
        } catch (error) {
            toast({ variant: 'destructive', title: 'Något gick fel.', description: error.response?.data?.error });
        } finally {
            setIsLoading(false);
            fetchClass(false);
        }
    };

    const handleChange = (e) => {
        e.preventDefault();
        isStraightCrooked ? submitStraightCrookedResult() : submitHeightLengthResult();
    };

    const incrementField = (fieldName) => {
        setFormData(prevState => ({
            ...prevState,
            [fieldName]: prevState[fieldName] + 1
        }));
    };

    const decrementField = (fieldName) => {
        setFormData(prevState => ({
            ...prevState,
            [fieldName]: prevState[fieldName] > 0 ? prevState[fieldName] - 1 : 0
        }));
    };

    const toggleAttempt = (index) => {
        setAttempts(prevAttempts =>
            prevAttempts.map((result, i) =>
                i === index ? (result === 'X' ? 'O' : 'X') : result
            )
        );
    };

    useEffect(() => {
        if (isDialogOpen) {
            // Find the heightLengthResults for the current round
            const levelResult = registration.heightLengthResults?.find(
                result => parseFloat(result.height_length) === parseFloat(round)
            );

            // Map attempt values to 'O' (success), 'X' (fail), or empty for null
            if (levelResult) {
                setAttempts([
                    levelResult.attempt1 === true ? 'O' : 'X',
                    levelResult.attempt2 === true ? 'O' : 'X',
                    levelResult.attempt3 === true ? 'O' : 'X',
                ]);
            } else {
                // Default to all failed if no results exist
                setAttempts(['X', 'X', 'X']);
            }
        }
    }, [isDialogOpen, registration, round]);

    const getLastRoundErrors = (registration, currentRound) => {
        const results = registration.straightCrookedResults;
        if (!results || results.length === 0 || currentRound <= 1) {
            return "inget resultat";
        }

        // Find the result for the previous round
        const previousRoundResult = results.find(result => result.round === currentRound - 1);

        return previousRoundResult
            ? previousRoundResult.errors
            : "inget resultat";
    };

    const getSumOfPreviousRoundErrors = (registration, currentRound) => {
        const results = registration.straightCrookedResults;
        if (!results || results.length === 0 || currentRound <= 1) {
            return 0; // No errors for rounds before the first round
        }

        // Filter results to include only those before the current round
        const previousRoundResults = results.filter(result => result.round < currentRound);

        // Sum the errors from all previous rounds
        return previousRoundResults.reduce((sum, result) => sum + result.errors, 0);
    };

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            {registration ? (
                dropdown ? (
                    <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                        <DialogTrigger className='flex items-center w-full'>
                            {updateOrMark.label}
                        </DialogTrigger>
                    </DropdownMenuItem>
                ) : (
                    <DialogTrigger className='flex items-center w-full' asChild>
                        <span className='text-blue-600 font-medium hover:underline cursor-pointer'>
                            {updateOrMark.label}
                        </span>
                    </DialogTrigger>
                )
            ) : (
                <span className='text-gray-400'>Ingen registrering tillgänglig</span>
            )}
            <DialogContent className="flex flex-col gap-6 max-h-dvh">
                <DialogHeader className='text-left'>
                    <DialogTitle>{updateOrMark.label} för {registration.bunny.name}</DialogTitle>
                    <DialogDescription>Ange information för resultatet.</DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={handleChange} className="flex flex-col gap-3">
                        <FormField
                            control={form.control}
                            name="dnf"
                            render={() => (
                                <FormItem className="space-y-0 grid gap-2">
                                    <FormControl>
                                        <Label className='flex gap-2 items-center'>
                                            <Checkbox
                                                className='size-6'
                                                checked={isDNF}
                                                onCheckedChange={(checked) => {
                                                    setIsDNF(checked);
                                                    if (checked) {
                                                        // Set errors and time when checked
                                                        setFormData((prevState) => ({
                                                            ...prevState,
                                                            errors: 99,
                                                            time: 1000,
                                                        }));
                                                    } else {
                                                        // Reset errors and time when unchecked
                                                        setFormData((prevState) => ({
                                                            ...prevState,
                                                            errors: 0, // or previous value if you have a default
                                                            time: '',  // or previous value if you have a default
                                                        }));
                                                    }
                                                }}
                                            />
                                            Ej i mål
                                        </Label>
                                    </FormControl>
                                    <FormDescription>
                                        Markera som Ej i mål om kaninen inte slutförde loppet.
                                    </FormDescription>
                                </FormItem>
                            )}
                        />
                        {!isDNF && (
                            <>
                                <FormField
                                    control={form.control}
                                    name="corrections"
                                    render={({ field }) => (
                                        <FormItem className="space-y-0 grid gap-2">
                                            <FormLabel>Antal korrigeringar</FormLabel>
                                            <FormControl>
                                                <div className='grid grid-cols-5 gap-3'>
                                                    <Button
                                                        type='button'
                                                        onClick={() => decrementField("corrections")}
                                                        className='col-span-2 h-12'
                                                    >
                                                        <MinusIcon className='size-6' />
                                                    </Button>
                                                    <Input
                                                        className='h-12 text-lg'
                                                        type="number"
                                                        value={formData.corrections}
                                                        onChange={(e) => setFormData(prevState => ({ ...prevState, corrections: e.target.value }))}
                                                        min='0'
                                                    // placeholder="Ange antal korrigeringar"
                                                    />
                                                    <Button
                                                        type='button'
                                                        onClick={() => incrementField("corrections")}
                                                        className='col-span-2 h-12'
                                                    >
                                                        <PlusIcon className='size-6' />
                                                    </Button>
                                                </div>
                                            </FormControl>
                                            <FormDescription>
                                                Tänk på att detta inte sparas i systemet.
                                            </FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                {isStraightCrooked ? (
                                    <>
                                        <FormField
                                            control={form.control}
                                            name="errors"
                                            render={({ field }) => (
                                                <FormItem className="space-y-0 grid gap-2">
                                                    <FormLabel>Antal fel</FormLabel>
                                                    <FormControl>
                                                        <div className='grid grid-cols-5 gap-3'>
                                                            <Button
                                                                type='button'
                                                                onClick={() => decrementField("errors")}
                                                                className='col-span-2 h-12'
                                                            >
                                                                <MinusIcon className='size-6' />
                                                            </Button>
                                                            <Input
                                                                className='h-12 text-lg'
                                                                type="number"
                                                                value={formData.errors}
                                                                onChange={(e) => setFormData(prevState => ({ ...prevState, errors: e.target.value }))}
                                                                max="98"
                                                                min='0'
                                                            />
                                                            <Button
                                                                type='button'
                                                                onClick={() => incrementField("errors")}
                                                                className='col-span-2 h-12'
                                                            >
                                                                <PlusIcon className='size-6' />
                                                            </Button>
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <p className='text-sm'>Kaninen hade <b>{getLastRoundErrors(registration, round)}</b> fel föregående omgång och har totalt <b>{getSumOfPreviousRoundErrors(registration, round)}</b> fel i klassen.</p>
                                        <FormField
                                            control={form.control}
                                            name="time"
                                            render={({ field }) => (
                                                <FormItem className="space-y-0 grid gap-2">
                                                    <FormLabel>Tid (sekunder)</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            value={formData.time}
                                                            onChange={(e) => setFormData(prevState => ({ ...prevState, time: e.target.value }))}
                                                            placeholder="Ange tid i sekunder"
                                                            step="0.01"
                                                            max="300"
                                                            autoComplete='off'
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </>
                                ) : (
                                    // Form for height and length
                                    <>
                                        {/* <FormField
                                            control={form.control}
                                            name="heightLength"
                                            render={({ field }) => (
                                                <FormItem className="space-y-0 grid gap-2">
                                                    <FormLabel>Höjd/Längd</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            value={formData.heightLength}
                                                            onChange={(e) => setFormData(prevState => ({ ...prevState, heightLength: e.target.value }))}
                                                            placeholder="Ange höjd/längd"
                                                            disabled={!!registration}
                                                        />

                                                    </FormControl>
                                                    <FormDescription>Värdet ska anges i centimeter med högst 2 decimaler.</FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        /> */}
                                        <FormField
                                            control={form.control}
                                            name="attempts"
                                            render={({ field }) => (
                                                <FormItem className="space-y-0 grid">
                                                    <FormLabel>Försök</FormLabel>
                                                    <FormControl>
                                                        <ToggleGroup type="multiple" className='justify-start size-20'>
                                                            {attempts.map((result, index) => (
                                                                <ToggleGroupItem
                                                                    className='p-8 text-2xl'
                                                                    key={index}
                                                                    value={`attempt-${index}`}
                                                                    aria-label={`Toggle attempt ${index + 1}`}
                                                                    onClick={() => toggleAttempt(index)}
                                                                >
                                                                    {result === "X" ? <Cross2Icon className='size-8' /> : <CircleIcon className='size-8' />}
                                                                </ToggleGroupItem>
                                                            ))}
                                                        </ToggleGroup>
                                                    </FormControl>
                                                    <FormDescription>Tryck för att ändra.</FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </>
                                )}
                            </>
                        )}
                        <div className='grid grid-cols-2 gap-3'>
                            <DialogClose asChild>
                                <Button type='button' variant='secondary'>Avbryt</Button>
                            </DialogClose>
                            <Button
                                type="submit"
                                disabled={isLoading}>
                                {isLoading ? <UpdateIcon className='loader-spinner' /> : <>{updateOrMark.label}</>}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
